import axios from 'axios';
import { fetchBaseUrl } from 'core/utils/common';
import { getUserIdFromToken } from '../utils/incidentUtils';
import { getCsrfTokenFromCookie } from "core/utils/csrf";

const baseURL = fetchBaseUrl({port: 8000});
const API_BASE_URL = `${baseURL}/socai/api`;

export interface FilePayload {
  file_content: string;
  file_name: string;
  mime_type: string;
  file_size: number;
}

export interface UploadResponse {
  status: number;
  data: any;
}

// Function to handle auth token
export const getAuthToken = async () => {
  // This function should be called from a React component context
  // where AuthContext is properly consumed
  return 'dev-key'; // Replace with actual token mechanism that doesn't use hooks
};

// Fetch capabilities/tasks
export const fetchCapabilities = async (amsBearerToken: string) => {
  const token = amsBearerToken;
  
  try {
    const response = await axios.get(`${API_BASE_URL}/tasks`, {
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': token
      }
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching capabilities:", error);
    throw error;
  }
};

// Fetch conversations
export const fetchConversations = async (amsBearerToken: string) => {
  const token = amsBearerToken;
let userId = await getUserIdFromToken(amsBearerToken);
  try {
    const csrfToken = getCsrfTokenFromCookie();
    const response = await axios.get(`${API_BASE_URL}/context/conversations?user_id=${userId}`, {
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': token,
        'X-CSRF-Token': csrfToken,
      }
    });
    return response.data.conversations;
  } catch (error) {
    console.error("Error fetching conversations:", error);
    throw error;
  }
};

// Upload file
export const uploadFile = async (userInput: string, conversationId: string, file: FilePayload, amsBearerToken: string): Promise<UploadResponse> => {
  try {
    const token = amsBearerToken;
    let userId = await getUserIdFromToken(amsBearerToken);
    const response = await axios.post(
      `${API_BASE_URL}/upload/orchestrator?user_id=${userId}`,
      {
        user_input: userInput,
        conversation_id: conversationId,
        file: file
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': token
        },
        params: { stream: false }
      }
    );
    return response.data.response;
  } catch (error) {
    console.error("Error uploading file:", error);
    throw error;
  }
};

// Send chat message
// ...existing code...

export const sendMessage = async ({userInput, conversationId, amsBearerToken}: {userInput: string, conversationId: string, amsBearerToken: string}) => {
  try {
    const token = amsBearerToken;
    const csrfToken = getCsrfTokenFromCookie();
    let userId = await getUserIdFromToken(amsBearerToken);
    const response = await axios.post(
      `${API_BASE_URL}/serve?user_id=${userId} `,
      {
        user_input: userInput,
        conversation_id: conversationId,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': token,
          'X-CSRF-Token': csrfToken
        },
        params: { stream: false }
      }
    );
    return response.data.response || [];
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

export const executeTask = async (taskId: string, amsBearerToken: string) => {
  try {
    const token = amsBearerToken;
    const csrfToken = getCsrfTokenFromCookie();
    let userId = await getUserIdFromToken(amsBearerToken);
    const response = await axios.post(
      `${API_BASE_URL}/task/${taskId}?user_id=${userId}`,
      {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': token,
          'X-CSRF-Token': csrfToken
        }
      }
    );
    return response.data.response || [];
  } catch (error) {
    console.error('Error executing task:', error);
    throw error;
  }
};
// Categorize conversations by date
export const categorizeConversations = (conversations: any[]) => {
  const categories: Record<string, any[]> = {
    Today: [],
    Yesterday: [],
    "Past Week": [],
    "Last Month": [],
    Older: [],
  };

  const now = new Date();
  conversations.forEach((conversation) => {
    const conversationDate = new Date(conversation.updated_at);
    const diffInDays = Math.floor(
      (now.getTime() - conversationDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (diffInDays === 0) {
      categories.Today.push(conversation);
    } else if (diffInDays === 1) {
      categories.Yesterday.push(conversation);
    } else if (diffInDays <= 7) {
      categories["Past Week"].push(conversation);
    } else if (diffInDays <= 30) {
      categories["Last Month"].push(conversation);
    } else {
      categories.Older.push(conversation);
    }
  });

  return categories;
};

export interface Chat {
  prompt: string;
  response: string[];
}

export const getMessageByConvoId = async (conversation_id: string, amsBearerToken: string) => {
  try {
    const token = amsBearerToken;
    let userId = await getUserIdFromToken(amsBearerToken);
    const response = await axios.get(
      `${API_BASE_URL}/conversation/${conversation_id}/messages?user_id=${userId}`,
      {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': token
        }
      }
    );
    
    const messages = response.data.messages || [];
    let transformedChats: Chat[] = [];
    
    // Group messages into prompt-response pairs
    for (let i = 0; i < messages.length; i++) {
      if (messages[i].role === 'user') {
        transformedChats.push({
          prompt: messages[i].content,
          response: messages[i + 1]?.role === 'assistant' ? [messages[i + 1].content] : []
        });
      }
    }

    // Return array of chat objects
    return {
      chats: transformedChats
    };
  } catch (error) {
    console.error('Error fetching messages:', error);
    throw error;
  }
};

export async function fetchWorkflowSteps(userInput: string, amsBearerToken: string) {
  const token = amsBearerToken;
  const csrfToken = getCsrfTokenFromCookie();
  let userId = await getUserIdFromToken(amsBearerToken);
  const response = await fetch(
    `${API_BASE_URL}/workflow/security_analysis?stream=true&user_id=${userId}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-API-Key": token,
        "X-CSRF-Token": csrfToken,
      },
      body: JSON.stringify({
        user_input: userInput,
        conversationid: ""
      }),
    }
  );

  if (!response.body) {
    console.error("ReadableStream not supported");
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let result = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    const chunk = decoder.decode(value, { stream: true });
    result += chunk;
    // Show partial data
    console.log(chunk);
    // Optional: update UI state with result so far
  }

  console.log("Stream complete:", result);
  return result;
}

export const fetchTableData = async (userInput: string, taskName:string, signal: AbortSignal, amsBearerToken: string) => {
  const token = amsBearerToken;
  const csrfToken = getCsrfTokenFromCookie();
  try {
    let userId = await getUserIdFromToken(amsBearerToken);
    const response = await fetch(`${API_BASE_URL}/task/${taskName}?stream=true&user_id=${userId}`, {
      method: 'POST',
     headers: {
        "Content-Type": "application/json",
        "X-API-Key": token,
        "X-CSRF-Token": csrfToken,
      },

      body: JSON.stringify({ 
        user_input: userInput 
      }),
      signal
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error('Response body is null');

    return new ReadableStream({
      async start(controller) {
        const decoder = new TextDecoder();
        let buffer = '';

        try {
          while (true) {
            const { done, value } = await reader.read();
            
            if (done) {
              if (buffer) {
                try {
                  const data = JSON.parse(buffer);
                  controller.enqueue(data);
                } catch (e) {
                  console.error('Error parsing final buffer:', e);
                }
              }
              break;
            }

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (line.trim()) {
                try {
                  const data = JSON.parse(line);
                  controller.enqueue(data);
                } catch (e) {
                  console.error('Error parsing stream data:', e);
                }
              }
            }
          }
        } catch (error) {
          controller.error(error);
        } finally {
          reader.releaseLock();
          controller.close();
        }
      },
      cancel() {
        reader.cancel();
      }
    });

  } catch (error) {
    console.error('Error fetching table data:', error);
    throw error;
  }
};